import { defineStore } from 'pinia'
import { useStatesStore } from './states-store'
import { DateTime } from 'luxon'
import {
  loadSurveysAPI,
  loadSurveyDetailsAPI,
  loadSurveyDetailsWithFilteredCategoriesAPI
} from '@/services/surveys-service'
import { requestTypes } from '@/constants/request-types'
import { sortedGroups } from '@/helpers/results-helper'
import { allHistoryYear } from '@/constants/time-types'

const aggregateCoveringSurvey = ({ surveyDetails }) => {
  if (!!surveyDetails.details.aggregate) {
    return null // already an aggregate
  } else {
    const surveyStartDate = DateTime.fromISO(surveyDetails.details.start_date)
    const surveyEndDate = DateTime.fromISO(surveyDetails.details.end_date)
    const aggregateSurveysCoveringSurvey = surveyDetails.allSurveysInfo.filter(
      (s) => {
        if (!!s.details.aggregate) {
          const w = s.details.aggregate.config.timeWindow
          const fromDate = w.from_date
            ? DateTime.fromISO(w.from_date)
            : DateTime.local()
          const olderDate = fromDate.minus({
            [w.unit.toLowerCase()]: w.amount
          })
          return (
            (surveyStartDate <= fromDate && surveyStartDate >= olderDate) ||
            (surveyEndDate <= fromDate && surveyEndDate >= olderDate)
          )
        } else {
          return false
        }
      }
    )
    return aggregateSurveysCoveringSurvey.length
      ? aggregateSurveysCoveringSurvey[0]
      : null
  }
}

export const useSurveysStore = defineStore('surveys-store', {
  state: () => ({
    error: {},
    loading: {},
    surveys: [],
    surveyDetails: null,
    filteredSurveyDetails: null, // for alert actions with filtered actions
    historyLoadedUntilYear: null
  }),
  getters: {
    getSurveys: (state) => {
      return state.surveys
    },
    getGroups: (state) => {
      return state.surveyDetails?.details.groups || []
    },
    getCategories: (state) => {
      return state.surveyDetails?.details.categories || []
    },
    getSurveyDetails: (state) => {
      return state.surveyDetails
    },
    getParticipationSettingsForGroup: (state) => () => {
      const statesStore = useStatesStore()
      const organizationName = statesStore.getOrganizationName
      const groupName = statesStore.getSelectedGroup.name
      if (groupName === organizationName) {
        return state.surveyDetails.details.resultViews.participation || {}
      } else {
        const group =
          state.surveyDetails.details.groups.find(
            (g) => g.name === groupName
          ) || {}
        if (group.config?.results?.participation) {
          return group.config.results.participation
        } else {
          return state.surveyDetails.details.resultViews.participation || {}
        }
      }
    },

    /*
      RULES:
      - If:
        * there are aggregates covering requested survey
        * and requested survey is not healthscreening
        * and requested survey is not custom
      then go to first available aggregate
      - Else go to requested survey
    */
    findSuitableSurveyInStore:
      (state) =>
      ({ surveyDetails }) => {
        // When we only have "current" surveys loaded, it might be that the aggregate
        // we expect is not in the list of current surveys.
        // The only way we can solve this is by keeping aggregate inside the survey itself
        const surveyIsHealthScreening =
          surveyDetails.details.config?.variant === 'friskkollen'
        const surveyIsCustom = surveyDetails.details.custom
        const aggregate = aggregateCoveringSurvey({
          surveyDetails: surveyDetails || state.surveyDetails
        })
        return !surveyIsHealthScreening && !surveyIsCustom && aggregate
      }
  },
  // maybe convert to return promises to data-loader can await the calls
  actions: {
    clear() {
      this.loading = {}
      this.error = {}
      this.surveyDetails = null
    },
    setHistoryLoadedUntilYear(year) {
      this.historyLoadedUntilYear = year
    },
    setSurveyDetails(surveyDetails) {
      this.surveyDetails = surveyDetails
      this.loading[requestTypes.SURVEY_DETAILS] = false
    },
    async loadSurveysAction({ currentSurveysOnly }) {
      this.loading[requestTypes.SURVEYS] = true
      try {
        const surveysResult = await loadSurveysAPI({
          currentSurveysOnly
        })
        this.surveys = surveysResult.sort((a, b) => {
          let aDate = a.details.end_date
          let bDate = b.details.end_date
          if (a.details.aggregate) {
            aDate =
              a.details.aggregate.config.timeWindow.from_date ||
              DateTime.local().toISO()
          }
          if (b.details.aggregate) {
            bDate =
              b.details.aggregate.config.timeWindow.from_date ||
              DateTime.local().toISO()
          }
          // Sort in descending order (most recent end_date first)
          return bDate.localeCompare(aDate)
        })
      } catch (error) {
        this.error[requestTypes.SURVEYS] = error
        throw error
      } finally {
        this.loading[requestTypes.SURVEYS] = false
      }
    },
    async loadSurveyDetailsAction({ surveyName, currentSurveysOnly }) {
      this.loading[requestTypes.SURVEY_DETAILS] = true
      try {
        this.surveyDetails = await loadSurveyDetailsAPI({
          surveyName,
          currentSurveysOnly
        })

        this.surveyDetails.oldSurveysInfo =
          this.surveyDetails.oldSurveysInfo.sort((a, b) =>
            a.details.end_date.localeCompare(b.details.end_date)
          )
        this.surveyDetails.details.groups = sortedGroups(
          this.surveyDetails.details.groups
        )
      } catch (error) {
        this.error[requestTypes.SURVEY_DETAILS] = error
      } finally {
        // if currentSurveysOnly is numeric, update its value in the story
        if (typeof currentSurveysOnly === 'string') {
          if (currentSurveysOnly.includes('-')) {
            const [startYear, endYear] = currentSurveysOnly
              .split('-')
              .map(Number)
            this.historyLoadedUntilYear = startYear
          }
        } else if (typeof currentSurveysOnly === 'number') {
          this.historyLoadedUntilYear = currentSurveysOnly
        } else if (currentSurveysOnly === true) {
          const currentYear = new Date().getFullYear() % 100
          this.historyLoadedUntilYear = currentYear
        } else if (currentSurveysOnly === false) {
          this.historyLoadedUntilYear = allHistoryYear
        }
        this.loading[requestTypes.SURVEY_DETAILS] = false
      }
    },
    async loadFilteredSurveyDetailsAction({ surveyName, currentSurveysOnly }) {
      this.loading[requestTypes.FILTERED_SURVEY_DETAILS] = true
      try {
        this.filteredSurveyDetails =
          await loadSurveyDetailsWithFilteredCategoriesAPI({
            surveyName,
            currentSurveysOnly
          })
      } catch (error) {
        this.error[requestTypes.FILTERED_SURVEY_DETAILS] = error
      } finally {
        this.loading[requestTypes.FILTERED_SURVEY_DETAILS] = false
      }
    }
  }
})
